import { compressImage, tooLargeRaw, tooLargeCompressed, fileExt, LONG_CACHE, PHOTO_MAX_SIDE } from "@/lib/image-compress";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { bannersTable, SECTION_PRESETS, type HomeBanner } from "@/lib/home-banners";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export const Route = createFileRoute("/_authenticated/admin/banners")({
  component: AdminBannersPage,
});

type LinkMode = "event" | "section" | "external";

function AdminBannersPage() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    (async () => {
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id;
      if (!uid) {
        navigate({ to: "/home" });
        return;
      }
      const { data: prof } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("user_id", uid)
        .maybeSingle();
      if (!(prof as { is_admin?: boolean } | null)?.is_admin) {
        setAuthorized(false);
        return;
      }
      setAuthorized(true);
    })();
  }, [navigate]);

  const { data: banners = [], isLoading } = useQuery({
    queryKey: ["admin", "home-banners"],
    enabled: authorized === true,
    queryFn: async () => {
      const { data, error } = await bannersTable()
        .select("*")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as HomeBanner[];
    },
  });

  const [editing, setEditing] = useState<HomeBanner | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const toggleActive = useMutation({
    mutationFn: async (b: HomeBanner) => {
      const { error } = await bannersTable().update({ is_active: !b.is_active }).eq("id", b.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "home-banners"] });
      qc.invalidateQueries({ queryKey: ["home-banners"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Не удалось сохранить"),
  });

  const removeBanner = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await bannersTable().delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Баннер удалён");
      qc.invalidateQueries({ queryKey: ["admin", "home-banners"] });
      qc.invalidateQueries({ queryKey: ["home-banners"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Не удалось удалить"),
  });

  if (authorized === null) {
    return <div className="mx-auto max-w-3xl px-4 py-8 text-sm text-td-muted">Проверка доступа…</div>;
  }
  if (authorized === false) {
    return <div className="mx-auto max-w-3xl px-4 py-8 text-sm text-td-muted">Нет доступа.</div>;
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-8">
      <button
        type="button"
        onClick={() => navigate({ to: "/profile" })}
        className="mb-4 text-sm text-td-muted hover:text-td-dark"
      >
        ← Назад
      </button>
      <h1 className="mb-1 text-2xl font-semibold text-td-dark">Баннеры главной</h1>
      <p className="mb-5 text-sm text-td-muted">
        Показываются каруселью в самом верху главной страницы. Меньше «порядок показа» — раньше в ленте.
      </p>

      {formOpen ? (
        <BannerForm
          banner={editing}
          onClose={() => {
            setFormOpen(false);
            setEditing(null);
          }}
        />
      ) : (
        <Button
          className="cta-solid mb-5"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          Добавить баннер
        </Button>
      )}

      {isLoading ? (
        <div className="text-sm text-td-muted">Загружаем…</div>
      ) : banners.length === 0 ? (
        <div className="rounded-2xl border border-td-line bg-white p-6 text-center text-sm text-td-muted">
          Баннеров пока нет.
        </div>
      ) : (
        <div className="space-y-3">
          {banners.map((b) => (
            <div
              key={b.id}
              className="flex items-center gap-3 rounded-2xl border border-td-line bg-white p-3"
            >
              <img decoding="async"
                src={b.image_url}
                alt=""
                className="h-14 w-24 shrink-0 rounded-xl object-cover"
                loading="lazy"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-td-dark">{b.title}</div>
                <div className="truncate text-xs text-td-muted">{b.subtitle || "—"}</div>
                <div className="truncate text-[11px] text-td-muted">
                  #{b.sort_order} · {b.link_url}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Switch
                  checked={b.is_active}
                  onCheckedChange={() => toggleActive.mutate(b)}
                  aria-label="Активен"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditing(b);
                    setFormOpen(true);
                  }}
                >
                  Изм.
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (window.confirm(`Удалить баннер «${b.title}»?`)) removeBanner.mutate(b.id);
                  }}
                >
                  Удалить
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}

function detectMode(url: string): LinkMode {
  if (url.startsWith("/events?event=")) return "event";
  if (url.startsWith("/")) return "section";
  return "external";
}

function BannerForm({ banner, onClose }: { banner: HomeBanner | null; onClose: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const [title, setTitle] = useState(banner?.title ?? "");
  const [subtitle, setSubtitle] = useState(banner?.subtitle ?? "");
  const [imageUrl, setImageUrl] = useState(banner?.image_url ?? "");
  const [ctaLabel, setCtaLabel] = useState(banner?.cta_label ?? "Смотреть");
  const [linkUrl, setLinkUrl] = useState(banner?.link_url ?? "");
  const [sortOrder, setSortOrder] = useState<string>(String(banner?.sort_order ?? 0));
  const [isActive, setIsActive] = useState(banner?.is_active ?? true);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState<LinkMode>(banner ? detectMode(banner.link_url) : "event");
  const [eventQuery, setEventQuery] = useState("");

  const { data: eventResults = [] } = useQuery({
    queryKey: ["admin", "banner-event-search", eventQuery],
    enabled: mode === "event" && eventQuery.trim().length >= 2,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, title, city, event_date")
        .ilike("title", `%${eventQuery.trim()}%`)
        .limit(8);
      if (error) throw error;
      return (data ?? []) as { id: string; title: string; city: string | null; event_date: string | null }[];
    },
  });

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Можно загружать только изображения");
      return;
    }
    if (tooLargeRaw(file.size)) {
      toast.error("Размер файла не должен превышать 5 МБ");
      return;
    }
    setUploading(true);
    try {
      const upFile = await compressImage(file, { maxSide: PHOTO_MAX_SIDE });
      if (tooLargeCompressed(upFile.size, 5)) throw new Error("Размер файла не должен превышать 5 МБ");
      const ext = fileExt(upFile);
      const path = `${user.id}/home-banners/${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from("avatars")
        .upload(path, upFile, { upsert: true, contentType: upFile.type, cacheControl: LONG_CACHE });
      if (error) throw error;
      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      setImageUrl(data.publicUrl);
      toast.success("Картинка загружена");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Не удалось загрузить картинку");
    } finally {
      setUploading(false);
    }
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!title.trim()) throw new Error("Укажи заголовок");
      if (!imageUrl) throw new Error("Загрузи картинку");
      if (!linkUrl.trim()) throw new Error("Укажи, куда ведёт баннер");
      const payload = {
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        image_url: imageUrl,
        link_url: linkUrl.trim(),
        cta_label: ctaLabel.trim() || "Смотреть",
        is_active: isActive,
        sort_order: Number(sortOrder) || 0,
      };
      if (banner) {
        const { error } = await bannersTable().update(payload).eq("id", banner.id);
        if (error) throw error;
      } else {
        const { error } = await bannersTable().insert({ ...payload, created_by: user?.id ?? null });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success("Сохранено");
      qc.invalidateQueries({ queryKey: ["admin", "home-banners"] });
      qc.invalidateQueries({ queryKey: ["home-banners"] });
      onClose();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Не удалось сохранить"),
  });

  return (
    <div className="mb-6 space-y-4 rounded-2xl border border-td-line bg-white p-4">
      <h2 className="text-lg font-semibold text-td-dark">
        {banner ? "Редактировать баннер" : "Новый баннер"}
      </h2>

      <div>
        <Label className="mb-1 block text-sm">Заголовок</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Большое знакомство" />
      </div>

      <div>
        <Label className="mb-1 block text-sm">Подзаголовок</Label>
        <Input
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          placeholder="Анкара, Бурса, Измир, Анталья, Аланья"
        />
      </div>

      <div>
        <Label className="mb-1 block text-sm">Картинка</Label>
        {imageUrl ? (
          <img loading="lazy" decoding="async" src={imageUrl} alt="" className="mb-2 aspect-[16/9] w-full max-w-sm rounded-xl object-cover" />
        ) : null}
        <label className="inline-flex cursor-pointer items-center rounded-full border border-td-line px-4 py-2 text-sm text-td-dark">
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
          {uploading ? "Загрузка…" : imageUrl ? "Заменить" : "Загрузить"}
        </label>
        <p className="mt-1 text-xs text-td-muted">Рекомендуем широкое фото, например 1600×900</p>
      </div>

      <div>
        <Label className="mb-1 block text-sm">Текст кнопки</Label>
        <Input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} placeholder="Смотреть" />
      </div>

      <div>
        <Label className="mb-1 block text-sm">Куда ведёт баннер</Label>
        <div className="mb-2 flex flex-wrap gap-2">
          {(
            [
              ["event", "Конкретное мероприятие"],
              ["section", "Раздел приложения"],
              ["external", "Внешняя ссылка"],
            ] as [LinkMode, string][]
          ).map(([m, label]) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={
                "rounded-full px-3 py-1.5 text-xs font-medium transition " +
                (mode === m ? "bg-td-teal text-white" : "border border-td-line text-td-dark")
              }
            >
              {label}
            </button>
          ))}
        </div>

        {mode === "event" ? (
          <div>
            <Input
              value={eventQuery}
              onChange={(e) => setEventQuery(e.target.value)}
              placeholder="Найти мероприятие по названию"
            />
            {eventResults.length > 0 ? (
              <div className="mt-2 overflow-hidden rounded-xl border border-td-line">
                {eventResults.map((ev) => (
                  <button
                    key={ev.id}
                    type="button"
                    onClick={() => {
                      setLinkUrl(`/events?event=${ev.id}`);
                      setEventQuery(ev.title);
                    }}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-td-soft/60"
                  >
                    <span className="truncate text-td-dark">{ev.title}</span>
                    <span className="shrink-0 text-xs text-td-muted">{ev.city ?? ""}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : mode === "section" ? (
          <div className="flex flex-wrap gap-2">
            {SECTION_PRESETS.map((s) => (
              <button
                key={s.url}
                type="button"
                onClick={() => setLinkUrl(s.url)}
                className={
                  "rounded-full px-3 py-1.5 text-xs font-medium transition " +
                  (linkUrl === s.url ? "bg-td-teal text-white" : "border border-td-line text-td-dark")
                }
              >
                {s.label}
              </button>
            ))}
          </div>
        ) : (
          <Input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://…"
          />
        )}

        <p className="mt-2 text-xs text-td-muted">Ссылка: {linkUrl || "не выбрана"}</p>
      </div>

      <div className="flex items-center gap-4">
        <div className="w-32">
          <Label className="mb-1 block text-sm">Порядок показа</Label>
          <Input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 pt-6">
          <Switch checked={isActive} onCheckedChange={setIsActive} aria-label="Активен" />
          <span className="text-sm text-td-dark">Активен</span>
        </div>
      </div>

      <div className="flex gap-2">
        <Button className="cta-solid" onClick={() => save.mutate()} disabled={save.isPending || uploading}>
          {save.isPending ? "Сохраняем…" : "Сохранить"}
        </Button>
        <Button variant="outline" onClick={onClose}>
          Отмена
        </Button>
      </div>
    </div>
  );
}
